import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { toast } from 'react-toastify'

import {
    Button,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Popover,
    Select,
    Switch,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'

import { Container, SideDrawerContainer } from '../../components'
import {
    useAppDispatch,
    useSchedule,
    useUser,
    useDebounce,
    useInspection,
} from '../../hooks'
import { Inspection, InspectionType, Schedule } from '../../models'
import {
    InventoryInspectionListAxiosReq,
    RootState,
    getInspectionList,
    setPaginatedInspectionList,
} from '../../store'
import { axiosInstance, isAccountManager } from '../../helpers'
import { InspectionFormDrawer } from './InspectionForm'
import { Mail, CloudUpload, LocalShipping } from '@material-ui/icons'
import { InspectionListTable } from './InspectionListTable'
import { CreateInspectionModal } from './CreateInspectionModal'
import { UploadFacilitiesInspectionModal } from './UploadFacilitiesInspectionModal'
import { useHistory } from 'react-router-dom'
import { Routes } from '../../navigation/config/routes'
import { SearchField } from '../../components/SearchField'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    scheduleList: Schedule[]
    inspectionTypeList: InspectionType[]
    setOpenInspectionConfiguration: (
        value: React.SetStateAction<boolean>,
    ) => void
}

export const InspectionPage = (props: Props) => {
    const {
        scheduleList,
        inspectionTypeList,
        setOpenInspectionConfiguration,
    } = props

    const [archivedCount, setArchivedCount] = useState(0)

    const { fetchArchivedInspectionCount } = useInspection(0)

    const dispatch = useAppDispatch()

    const inspectionList = useSelector(
        (state: RootState) => state.inspection.inspectionList,
    )

    const paginatedInspectionList = useSelector(
        (state: RootState) => state.inspection.paginatedInspectionList,
    )

    const inspectionPagination = useSelector(
        (state: RootState) => state.inspection.inspectionPagination,
    )

    const isLoading = useSelector(
        (state: RootState) => state.inspection.isLoading.GET_INSPECTION_LIST,
    )

    const { getScheduleList } = useSchedule()

    const [searchField, setSearchField] = useState('')

    // Debounce the search field to avoid excessive API calls
    const debouncedSearchField = useDebounce(searchField, 500)

    const theme = useTheme()

    const currentYear = new Date().getFullYear()

    const [createInspectionModalOpen, setCreateInspectionModalOpen] = useState(
        false,
    )

    const [
        uploadFacilitiesInspectionModalOpen,
        setUploadFacilitiesInspectionModalOpen,
    ] = useState(false)

    const [drawerOpen, setDrawerOpen] = useState(false)
    const [selectedInspection, setSelectedInspection] = useState<
        Inspection | undefined
    >()

    const [reportYear, setReportYear] = useState(currentYear)

    const [archivedFilter, setArchivedFilter] = useState(false)

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [sortBy, setSortBy] = useState<string>('start_date')
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)

    const popOverOpen = Boolean(anchorEl)

    const openPopover = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const closePopover = () => {
        setAnchorEl(null)
    }

    const yearOptions = [currentYear]
    let yearOption = currentYear
    // 2021 because it was the first year of ezturn inspections
    while (yearOption > 2021) {
        yearOption--
        yearOptions.push(yearOption)
    }

    const handleDrawerClose = () => {
        setDrawerOpen(false)
        setSelectedInspection(undefined)
    }

    const { userList, workspaceUser, organization } = useUser()
    const history = useHistory()

    const [
        selectedInspectionType,
        setSelectedInspectionType,
    ] = useState<number>(-1)

    useEffect(() => {
        fetchArchivedInspectionCount().then((count) => {
            setArchivedCount(count)
        })
    }, [])

    // Function to fetch inspections with pagination and sorting
    const fetchInspections = useCallback(() => {
        const params: any = {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            sort_by: sortDirection === 'desc' ? `-${sortBy}` : sortBy,
            active: !archivedFilter,
        }

        if (debouncedSearchField.trim() !== '') {
            params.search = debouncedSearchField.trim()
        }

        if (selectedInspectionType !== -1) {
            params.inspection_type_id = selectedInspectionType
        }

        dispatch(getInspectionList({ params }))
    }, [
        dispatch,
        page,
        rowsPerPage,
        sortBy,
        sortDirection,
        archivedFilter,
        debouncedSearchField,
        selectedInspectionType,
    ])

    // Fetch inspections when dependencies change
    useEffect(() => {
        fetchInspections()
    }, [fetchInspections])

    const filteredInspectionList = useMemo(() => {
        return inspectionList?.filter(
            (inspection) =>
                inspection.active !== archivedFilter &&
                (searchField.trim() === '' ||
                    inspection.name
                        .toLowerCase()
                        .includes(searchField.toLowerCase())) &&
                (selectedInspectionType === -1 ||
                    inspection.inspection_type.id === selectedInspectionType),
        )
    }, [inspectionList, archivedFilter, searchField, selectedInspectionType])

    // Use paginated data if available, otherwise fall back to filtered list
    const displayInspectionList =
        paginatedInspectionList || filteredInspectionList

    // Pagination handlers
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    // Sorting handler
    const handleSort = (property: string, direction: 'asc' | 'desc') => {
        setSortBy(property)
        setSortDirection(direction)
        setPage(0) // Reset to first page when sorting
    }

    // Handler for when an inspection is archived
    const handleInspectionArchived = useCallback(
        (archivedInspectionId: number) => {
            // Simply reduce the archived count by 1
            setArchivedCount((prevCount) => prevCount + 1)

            // Remove the archived inspection from the paginated list
            if (paginatedInspectionList) {
                const updatedList = paginatedInspectionList.filter(
                    (inspection) => inspection.id !== archivedInspectionId,
                )
                // Update the store with the filtered list and reduce count by 1
                dispatch(
                    setPaginatedInspectionList(
                        updatedList,
                        (inspectionPagination?.count || 0) - 1,
                        inspectionPagination?.next,
                        inspectionPagination?.previous,
                    ),
                )
            }
        },
        [paginatedInspectionList, dispatch, inspectionPagination],
    )

    // Handler for when an inspection is deleted
    const handleInspectionDeleted = useCallback(
        (deletedInspectionId: number) => {
            // Remove the deleted inspection from the paginated list
            if (paginatedInspectionList) {
                const updatedList = paginatedInspectionList.filter(
                    (inspection) => inspection.id !== deletedInspectionId,
                )
                // Update the store with the filtered list and reduce count by 1
                dispatch(
                    setPaginatedInspectionList(
                        updatedList,
                        (inspectionPagination?.count || 0) - 1,
                        inspectionPagination?.next,
                        inspectionPagination?.previous,
                    ),
                )
            }
        },
        [paginatedInspectionList, dispatch, inspectionPagination],
    )

    // Handler for when an inspection is updated
    const handleInspectionUpdated = useCallback(
        (updatedInspection: Inspection) => {
            // Update the specific inspection in the paginated list
            if (paginatedInspectionList) {
                const updatedList = paginatedInspectionList.map((inspection) =>
                    inspection.id === updatedInspection.id
                        ? updatedInspection
                        : inspection,
                )
                // Update the store with the updated list
                dispatch(
                    setPaginatedInspectionList(
                        updatedList,
                        inspectionPagination?.count || 0,
                        inspectionPagination?.next,
                        inspectionPagination?.previous,
                    ),
                )
            }
        },
        [paginatedInspectionList, dispatch, inspectionPagination],
    )

    // Handler for when a new inspection is created
    const handleInspectionCreated = useCallback(
        (newInspection: Inspection) => {
            // Check if the new inspection matches current filters
            const matchesActiveFilter = newInspection.active !== archivedFilter
            const matchesSearchFilter =
                debouncedSearchField.trim() === '' ||
                newInspection.name
                    .toLowerCase()
                    .includes(debouncedSearchField.toLowerCase())
            const matchesTypeFilter =
                selectedInspectionType === -1 ||
                newInspection.inspection_type.id === selectedInspectionType

            // Only add to list if it matches all current filters
            if (
                matchesActiveFilter &&
                matchesSearchFilter &&
                matchesTypeFilter
            ) {
                if (paginatedInspectionList) {
                    const updatedList = [
                        newInspection,
                        ...paginatedInspectionList,
                    ]
                    // Update the store with the new list and increment count by 1
                    dispatch(
                        setPaginatedInspectionList(
                            updatedList,
                            (inspectionPagination?.count || 0) + 1,
                            inspectionPagination?.next,
                            inspectionPagination?.previous,
                        ),
                    )
                }
            }
        },
        [
            paginatedInspectionList,
            dispatch,
            inspectionPagination,
            archivedFilter,
            debouncedSearchField,
            selectedInspectionType,
        ],
    )

    return (
        <SideDrawerContainer open={drawerOpen}>
            <Container
                style={{
                    flex: 1,
                    flexDirection: 'column',
                    height: 'calc(100vh - 160px)',
                }}
            >
                {/* Header */}
                <Container
                    style={{
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        minWidth: 0,
                    }}
                >
                    <Container style={{ flexDirection: 'column' }}>
                        <Container alignItems="center">
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                }}
                            >
                                Inspection
                            </span>

                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation()
                                    openPopover(e)
                                }}
                            >
                                <InfoIcon fontSize="small" color="action" />
                            </IconButton>
                        </Container>

                        <Popover
                            open={popOverOpen}
                            anchorEl={anchorEl}
                            onClose={closePopover}
                            anchorOrigin={{
                                horizontal: 'left',
                                vertical: 'bottom',
                            }}
                        >
                            <Container
                                direction="column"
                                style={{
                                    padding: theme.spacing(2),
                                }}
                            >
                                <Container
                                    style={{
                                        fontWeight: 600,
                                        fontSize: '16px',
                                    }}
                                >
                                    Steps to create an Inspection
                                </Container>
                                <Container>
                                    Step 1: Create or choose an Inspection Type
                                </Container>
                                <Container>
                                    Step 2: Create an Inspection
                                </Container>
                                <Container>
                                    Step 3: Assign an Inspection
                                </Container>
                            </Container>
                        </Popover>

                        <FormControlLabel
                            control={
                                <Switch
                                    value={archivedFilter}
                                    color="primary"
                                    onChange={() =>
                                        setArchivedFilter(!archivedFilter)
                                    }
                                />
                            }
                            labelPlacement="start"
                            label={`Show Archived (${archivedCount})`}
                            style={{ margin: 0 }}
                        />
                    </Container>

                    <div style={{ flex: 1 }} />
                    <FormControl
                        variant="outlined"
                        style={{ margin: theme.spacing(2), minWidth: 200 }}
                    >
                        <InputLabel id="inspection-type-label">
                            Inspection Type
                        </InputLabel>
                        <Select
                            labelId="inspection-type-label"
                            id="inspection-type-select"
                            value={selectedInspectionType}
                            onChange={(e) => {
                                setSelectedInspectionType(
                                    e.target.value as number,
                                )
                            }}
                            label="Inspection Type"
                            style={{ paddingLeft: theme.spacing(1) }}
                        >
                            <MenuItem value={-1}>
                                <span>All Inspection Types</span>
                            </MenuItem>
                            {[...inspectionTypeList]
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((type) => (
                                    <MenuItem
                                        key={`TYPE_SELECT__${type.id}`}
                                        value={type.id}
                                    >
                                        <span>{type.name}</span>
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <FormControl
                        variant="outlined"
                        style={{ margin: theme.spacing(2) }}
                    >
                        <InputLabel id={`report-year-label`}>
                            Report Year
                        </InputLabel>
                        <Select
                            labelId={`report-year-label`}
                            id={`report-year-select`}
                            value={reportYear}
                            onChange={(e) => {
                                setReportYear(e.target.value as number)
                            }}
                            label="Report Year"
                            style={{ paddingLeft: theme.spacing(1) }}
                        >
                            {yearOptions.map((year) => (
                                <MenuItem
                                    key={`YEAR_SELECT__${year}`}
                                    value={year}
                                >
                                    <span>{year}</span>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Tooltip title="Email Year Report">
                        <IconButton
                            onClick={() => {
                                const startOfYear = new Date(reportYear, 0)
                                const endOfYear = new Date(
                                    reportYear,
                                    11,
                                    31,
                                    23,
                                    59,
                                    59,
                                    999,
                                )

                                const req: InventoryInspectionListAxiosReq = {
                                    params: {
                                        lower_bound_date: startOfYear.toISOString(),
                                        upper_bound_date: endOfYear.toISOString(),
                                    },
                                }
                                axiosInstance.get(
                                    'inspection/inventory-inspection/create_report/',
                                    req,
                                )
                                toast.success(
                                    'You will receive an email with your report shortly!',
                                )
                            }}
                        >
                            <Mail fontSize="large" />
                        </IconButton>
                    </Tooltip>
                    {organization === 2305 && (
                        <Tooltip title="Upload Furniture Install Sheet">
                            <IconButton
                                onClick={() => {
                                    history.push(
                                        Routes.apartment.config
                                            .furnitureInstalls,
                                    )
                                }}
                            >
                                <LocalShipping fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {isAccountManager(workspaceUser) && (
                        <Tooltip title="Upload Facilities Inspection Sheet">
                            <IconButton
                                onClick={() => {
                                    setUploadFacilitiesInspectionModalOpen(true)
                                }}
                            >
                                <CloudUpload fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Button
                        variant="contained"
                        style={{
                            margin: theme.spacing(3, 0, 3, 2),
                            backgroundColor: '#008C85',
                            color: 'white',
                            textTransform: 'none',
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            setCreateInspectionModalOpen(true)
                        }}
                    >
                        + Add Inspection
                    </Button>
                    <Button
                        variant="contained"
                        style={{
                            margin: theme.spacing(3, 0, 3, 2),
                            backgroundColor: 'white',
                            color: theme.palette.primary.dark,
                            textTransform: 'none',
                            cursor: 'pointer',
                        }}
                        onClick={() => setOpenInspectionConfiguration(true)}
                    >
                        + Configure Inspection
                    </Button>
                    <SearchField
                        variant="outlined"
                        style={{
                            margin: theme.spacing(3, 0, 3, 2),
                            backgroundColor: 'white',
                        }}
                        value={searchField}
                        onChange={(e) => {
                            setSearchField(e.target.value)
                        }}
                        placeholder="Inspection Name"
                    />
                </Container>
                {/* End Header */}

                <InspectionListTable
                    inspectionList={displayInspectionList}
                    selectedInspection={selectedInspection}
                    setSelectedInspection={setSelectedInspection}
                    setDrawerOpen={setDrawerOpen}
                    active={archivedFilter === false}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={inspectionPagination?.count || 0}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    onSort={handleSort}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    isLoading={isLoading}
                    onInspectionArchived={handleInspectionArchived}
                    onInspectionDeleted={handleInspectionDeleted}
                    workspaceUser={workspaceUser}
                />
            </Container>

            <InspectionFormDrawer
                open={drawerOpen}
                handleClose={() => {
                    handleDrawerClose()
                }}
                inspectionTypeList={inspectionTypeList ?? []}
                scheduleList={scheduleList ?? []}
                getScheduleList={getScheduleList}
                selectedInspection={selectedInspection}
                onInspectionUpdated={handleInspectionUpdated}
                onInspectionCreated={handleInspectionCreated}
            />

            <CreateInspectionModal
                open={createInspectionModalOpen}
                scheduleList={scheduleList}
                inspectionTypeList={inspectionTypeList ?? []}
                userList={userList}
                onClose={() => setCreateInspectionModalOpen(false)}
                onInspectionCreated={handleInspectionCreated}
            />

            <UploadFacilitiesInspectionModal
                open={uploadFacilitiesInspectionModalOpen}
                onClose={() => setUploadFacilitiesInspectionModalOpen(false)}
            />
        </SideDrawerContainer>
    )
}
